import React, {useEffect, useRef, useState} from 'react';
import {Button, Space, Typography} from 'antd';
import {t} from '@lingui/core/macro';
import ApiService from '../../services/ApiService';
import {navigateTop} from '../../services/paiementLauncher';
import {getHostUrl, isEmbedded} from '../../services/embedHost';
import {checkoutIdentity, checkoutToken, loadStripeWalletSdk, walletIdentityMissing} from './stripeWalletProtocol';

const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
const endpoint = action => '/paiements/stripe-wallet/' + action;
const settled = result => ['COMPLETED', 'REFUNDED', 'REFUND_PENDING'].includes(result?.state) || result?.completed;

export default function StripeWalletButtons(props) {
    const current = useRef(props);
    current.current = props;
    const mount = useRef(null), alive = useRef(true), phase = useRef('idle');
    const attempt = useRef(null), preparation = useRef(null), pendingRef = useRef(false), cancelPending = useRef(false);
    const [caps, setCaps] = useState(null), [available, setAvailable] = useState(false);
    const [error, setError] = useState(null), [pending, setPending] = useState(false);
    const checkingRef = useRef(false);
    const detected = useRef({applePay: false, googlePay: false});
    const [checking, setChecking] = useState(false);
    const {enabled, organisation, assoc, amount, currency, selectedMethod} = props;

    const reportAvailability = value => {
        detected.current = value;
        current.current.onAvailability?.(value);
    };

    const busy = value => {if (alive.current) current.current.onBusy?.(value);};
    const post = (action, body) => ApiService.post(endpoint(action), body, {useProxy: !!current.current.authenticated});
    const origins = () => {
        let parentOrigin;
        try {if (isEmbedded() && getHostUrl()) parentOrigin = new URL(getHostUrl()).origin;} catch { /* configured iframe fallback is checked by the server */ }
        return {origin: window.location.origin, parentOrigin};
    };

    useEffect(() => {alive.current = true; return () => {alive.current = false;};}, []);
    useEffect(() => {
        if (!pendingRef.current && phase.current === 'idle') setError(null);
    }, [selectedMethod]);
    useEffect(() => {
        let canceled = false;
        setCaps(null); setAvailable(false);
        reportAvailability({applePay: false, googlePay: false});
        if (!enabled || !organisation || !assoc) return;
        (async () => {
            for (let count = 0; count < 4 && !canceled; count++) {
                try {
                    const result = await post('capabilities', {organisation, assoc, ...origins()});
                    if (canceled) return;
                    if (result?.success && (result.applePay || result.googlePay)) {setCaps(result); return;}
                    if (result?.state !== 'PREPARING') return;
                } catch {return;}
                await pause(3000);
            }
        })();
        return () => {canceled = true;};
    }, [enabled, organisation, assoc]);

    const checkStatus = async () => {
        if (!attempt.current) return;
        for (let count = 0; count < 12 && (alive.current || count === 0); count++) {
            try {
                const result = await post(cancelPending.current ? 'cancel' : 'status', {token: attempt.current.token});
                if (settled(result)) {
                    pendingRef.current = false; phase.current = 'settled';
                    if (alive.current) {setPending(false); current.current.onSuccess?.(result); navigateTop(result.url);}
                    return true;
                }
                if (['CANCELED', 'RETRYABLE'].includes(result?.state)) {
                    if (result.state === 'CANCELED') {attempt.current = null; cancelPending.current = false;}
                    pendingRef.current = false; phase.current = 'idle';
                    if (alive.current) setPending(false);
                    return false;
                }
            } catch { /* a webhook can own finalization: retry this same payment */ }
            await pause(750);
        }
        if (alive.current) {
            pendingRef.current = true; setPending(true);
            setError(t`La confirmation du paiement est en cours. Vérifiez son état avant de réessayer.`);
        }
        return false;
    };

    const abandon = async () => {
        if (!attempt.current) return true;
        cancelPending.current = true;
        return checkStatus();
    };

    useEffect(() => {
        if (!caps || !enabled || !mount.current) return;
        if (!Number.isSafeInteger(amount) || amount <= 0) {
            setAvailable(false); reportAvailability({applePay: false, googlePay: false});
            return;
        }
        let canceled = false, element;
        const release = () => {
            if (phase.current === 'settled') {busy(true); return;}
            phase.current = pendingRef.current ? 'pending' : 'idle'; busy(pendingRef.current);
        };
        const assertActive = () => {if (canceled || !alive.current) throw new Error('STALE_WALLET_ELEMENT');};
        (async () => {
            try {
                const Stripe = await loadStripeWalletSdk();
                if (canceled) return;
                const stripe = Stripe(caps.publishableKey, caps.stripeAccount ? {stripeAccount: caps.stripeAccount} : undefined);
                // Match the server's explicit card-only Intent contract. The
                // default automatic mode cannot confirm a fixed-method Intent.
                const elements = stripe.elements({mode: 'payment', amount, currency, locale: 'auto', allowedPaymentMethodTypes: ['card'], setupFutureUsage: 'off_session'});
                element = elements.create('expressCheckout', {
                    // Stripe owns the official wallet artwork and localization.
                    // Brand-only buttons also fit donations, registrations and purchases.
                    buttonType: {applePay: 'plain', googlePay: 'plain'},
                    buttonTheme: {applePay: 'black', googlePay: 'black'},
                    buttonHeight: 48,
                    emailRequired: true, billingAddressRequired: true, phoneNumberRequired: true,
                    paymentMethods: {applePay: caps.applePay && (!selectedMethod || selectedMethod === 'apple_pay') ? 'auto' : 'never',
                        googlePay: caps.googlePay && (!selectedMethod || selectedMethod === 'google_pay') ? 'auto' : 'never',
                        link: 'never', paypal: 'never', amazonPay: 'never', klarna: 'never'},
                    layout: {maxColumns: 2, maxRows: 0, overflow: 'never'},
                });
                element.on('ready', event => {
                    if (canceled) return;
                    const methods = event.availablePaymentMethods;
                    setAvailable(!!(methods?.applePay || methods?.googlePay));
                    reportAvailability(selectedMethod
                        ? {...detected.current, [selectedMethod === 'apple_pay' ? 'applePay' : 'googlePay']: !!methods?.[selectedMethod === 'apple_pay' ? 'applePay' : 'googlePay']}
                        : {applePay: !!methods?.applePay, googlePay: !!methods?.googlePay});
                });
                element.on('click', event => {
                    if (phase.current !== 'idle' || current.current.disabled || pendingRef.current || !['apple_pay', 'google_pay'].includes(event.expressPaymentType)) {event.reject(); return;}
                    if (selectedMethod === null || (selectedMethod && selectedMethod !== event.expressPaymentType)) {event.reject(); return;}
                    const missingIdentity = walletIdentityMissing(current.current.getIdentity?.());
                    if (missingIdentity.length) {
                        // Reject immediately to preserve Stripe's one-second click
                        // contract. Only civil identity is checked here: the wallet
                        // can still supply address, email and phone afterwards.
                        event.reject();
                        setError(t`Complétez les informations manquantes, puis réessayez avec Apple Pay ou Google Pay.`);
                        Promise.resolve().then(() => current.current.validateIdentity?.()).catch(() => {}).then(() => {
                            if (!canceled && alive.current) current.current.onMissing?.(missingIdentity);
                        });
                        return;
                    }
                    phase.current = 'open'; busy(true); setError(null);
                    // Stripe requires this within one second; the server work
                    // runs concurrently and is awaited before confirmation.
                    event.resolve();
                    preparation.current = (async () => {
                        const config = await current.current.buildConfig();
                        assertActive();
                        if (!config) throw new Error('INVALID_CONFIG');
                        config.platform = event.expressPaymentType === 'apple_pay' ? 'STRIPE.APPLEPAY' : 'STRIPE.GOOGLEPAY';
                        const identity = checkoutIdentity(config);
                        if (attempt.current && attempt.current.identity !== identity) {
                            await abandon();
                            if (attempt.current) throw new Error('PREVIOUS_PAYMENT_PENDING');
                        }
                        assertActive();
                        if (!attempt.current) attempt.current = {identity, platform: config.platform, token: checkoutToken()};
                        let result;
                        for (let i = 0; i < 8; i++) {
                            assertActive();
                            result = await post('prepare', {config, token: attempt.current.token, assoc, ...origins()});
                            if (!result?.pending) break;
                            await pause(500);
                        }
                        if (settled(result)) {
                            phase.current = 'settled';
                            if (alive.current) {current.current.onSuccess?.(result); navigateTop(result.url);}
                            return result;
                        }
                        if (!result?.success || result.amount !== amount || result.currency !== currency) throw new Error('PREPARATION_FAILED');
                        return result;
                    })();
                    // An abandoned wallet may never emit confirm. Keep a failed
                    // promise observed without displaying a false payment failure.
                    preparation.current.catch(() => {});
                });
                element.on('cancel', async () => {
                    if (phase.current !== 'open') return;
                    phase.current = 'canceling';
                    try {
                        await preparation.current?.catch(() => {});
                        await abandon();
                    } finally {release();}
                });
                element.on('confirm', async event => {
                    let confirming = false, stripeStarted = false, failureReported = false;
                    const paymentFailed = payload => {
                        // confirmPayment owns the native sheet once called.
                        // Before that, report at most once and observe an async
                        // rejection if the sheet was already disposed.
                        if (stripeStarted || failureReported) return;
                        failureReported = true;
                        try {Promise.resolve(event.paymentFailed(payload)).catch(() => {});} catch { /* disposed native sheet */ }
                    };
                    if (canceled) {paymentFailed(); return;}
                    phase.current = 'confirming';
                    try {
                        const prepared = await preparation.current;
                        if (settled(prepared)) {paymentFailed(); return;}
                        assertActive();
                        current.current.applyDetails(event.billingDetails || {});
                        await current.current.validate();
                        assertActive();
                        const config = await current.current.buildConfig();
                        assertActive();
                        // Account lookup and owner state can change independently
                        // of disabled form controls. Never confirm a stale contract.
                        if (!config || checkoutIdentity({...config, platform: attempt.current.platform}) !== attempt.current.identity) {
                            throw new Error('STRIPE_WALLET_CONTEXT_CHANGED');
                        }
                        const submitted = await elements.submit();
                        assertActive();
                        if (submitted.error) throw submitted.error;
                        const ready = await post('ready', {token: attempt.current.token, info: config.info, facturation: config.facturation});
                        if (settled(ready)) {paymentFailed(); await checkStatus(); return;}
                        assertActive();
                        if (ready?.error === 'STRIPE_WALLET_FIELDS_REQUIRED') {
                            throw Object.assign(new Error('MISSING_FIELDS'), {missing: true, fields: ready.fields});
                        }
                        if (!ready?.success) throw new Error('NOT_READY');
                        confirming = true; stripeStarted = true;
                        const result = await stripe.confirmPayment({elements, clientSecret: ready.clientSecret,
                            confirmParams: {return_url: ready.url}, redirect: 'if_required'});
                        if (result.error) {
                            // A transport/API failure can hide a completed charge.
                            // Only a definite input/card error unlocks a new try.
                            confirming = !['card_error', 'validation_error'].includes(result.error.type);
                            throw result.error;
                        }
                        await checkStatus();
                    } catch (e) {
                        paymentFailed({reason: e.errorFields || e.missing ? 'invalid_billing_address' : 'fail'});
                        if (confirming) {await checkStatus();}
                        else if (canceled || !alive.current) {await abandon();}
                        else if (e.errorFields || e.missing) {
                            setError(t`Complétez les informations manquantes, puis réessayez avec Apple Pay ou Google Pay.`);
                            current.current.onMissing?.(e.fields || e.errorFields?.map(field => field.name));
                        } else {
                            setError(t`Le paiement express n'a pas pu être lancé. Vos informations sont conservées.`);
                            // A known card/input failure can safely return to the
                            // ordinary checkout once its Intent was canceled.
                            await abandon();
                        }
                    } finally {release();}
                });
                element.mount(mount.current);
            } catch {if (!canceled) {setAvailable(false); reportAvailability({applePay: false, googlePay: false});}}
        })();
        return () => {
            canceled = true;
            // Destroying an Element does not cancel its server Intent. A confirm
            // already underway owns reconciliation until its outcome is known.
            const wasOpen = phase.current === 'open';
            if (wasOpen) phase.current = 'canceling';
            element?.destroy();
            if (wasOpen) {
                Promise.resolve(preparation.current).catch(() => {}).then(abandon).finally(release);
            }
        };
    }, [caps, enabled, amount, currency, assoc, organisation, selectedMethod]);

    if ((!enabled || !caps) && !pending && !error) return null;
    return <Space direction="vertical" size="small" style={{width: '100%', minWidth: 0}}>
        <div ref={mount} aria-hidden={!available || selectedMethod === null} style={{width: '100%', minWidth: 0,
            ...(!available || selectedMethod === null ? {height: 0, overflow: 'hidden'} : {})}}/>
        {error && <Typography.Text type={pending ? undefined : 'danger'} role="alert">{error}</Typography.Text>}
        {pending && <Button disabled={checking} loading={checking} onClick={async () => {
            if (checkingRef.current) return;
            checkingRef.current = true; setChecking(true); busy(true);
            try {await checkStatus();}
            finally {checkingRef.current = false; setChecking(false); busy(phase.current === 'settled' || pendingRef.current);}
        }}>{t`Vérifier le paiement`}</Button>}
    </Space>;
}
